import React, { useEffect, useState } from 'react';
import { Mail, Loader2, ExternalLink, User as UserIcon, FileText } from 'lucide-react';
import api from '../api/client';

/**
 * Wave 1u3 Phase B / G2 — Timeline emails per entreprise.
 *
 * Loads from GET /api/companies/{id}/email-threads which merges:
 *   • Manual `mailbox_thread_links` rows (user clicked "Rattacher cet email")
 *   • Auto-logged `crm_activities` of type email* with company_id set
 *
 * Each row shows subject, snippet, counterparty, date, and a button to open
 * the thread in /app/email?thread={thread_id}.
 */
export default function CompanyEmailsTab({ companyId, navigate }) {
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/companies/${companyId}/email-threads`)
      .then(({ data }) => {
        if (alive) setThreads(data?.threads || []);
      })
      .catch(() => alive && setThreads([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [companyId]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  };

  const openThread = (threadId) => {
    navigate(`/app/email?thread=${encodeURIComponent(threadId)}`);
  };

  const createQuoteFromThread = (t) => {
    // Phase B / G3 — pre-fill devis dialog with the company + subject as title
    navigate('/app/devis', {
      state: {
        prefillCreate: {
          company_id: companyId,
          title: t.subject || '',
          source_email_thread_id: t.thread_id,
        },
      },
    });
  };

  if (loading) {
    return (
      <div className="soft-card p-6 text-center text-[#6B6C85] text-[13px]" data-testid="company-emails-loading">
        <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
        Chargement des emails…
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="soft-card p-8 text-center" data-testid="company-emails-empty">
        <Mail className="w-10 h-10 text-[#C7D2FE] mx-auto mb-3" />
        <h3 className="text-[14.5px] font-bold text-[#1A1B2E] mb-1">Aucun email rattaché</h3>
        <p className="text-[12.5px] text-[#6B6C85] max-w-md mx-auto">
          Depuis la <strong>Boîte email</strong>, sélectionnez un fil de discussion et cliquez sur
          <em> « Rattacher à une entreprise »</em> — il apparaîtra ici dans la timeline.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="company-emails-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[15px] font-bold text-[#1A1B2E]">Timeline emails</h3>
          <p className="text-[12px] text-[#6B6C85]">
            {threads.length} {threads.length > 1 ? 'fils de discussion rattachés' : 'fil de discussion rattaché'}
          </p>
        </div>
      </div>

      <ol className="space-y-2" data-testid="company-emails-list">
        {threads.map((t) => (
          <li
            key={t.thread_id}
            className="soft-card p-4 hover:bg-[#FAFAFE] transition-colors"
            data-testid={`company-email-thread-${t.thread_id}`}
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#E0F2FE] text-[#0369A1] flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-[13.5px] font-semibold text-[#1A1B2E] truncate">
                    {t.subject || '(sans objet)'}
                  </span>
                  {t.source === 'manual' && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#EEF2FF] text-[#4F46E5]" data-testid="email-source-manual">
                      Manuel
                    </span>
                  )}
                  {t.source === 'auto' && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#ECFDF5] text-[#047857]" data-testid="email-source-auto">
                      Auto
                    </span>
                  )}
                </div>
                {(t.counterparty_email || t.counterparty_name) && (
                  <div className="text-[11.5px] text-[#6B6C85] mt-0.5 flex items-center gap-1">
                    <UserIcon className="w-3 h-3" />
                    {t.counterparty_name && <span className="font-medium">{t.counterparty_name}</span>}
                    {t.counterparty_email && <span className="truncate">&lt;{t.counterparty_email}&gt;</span>}
                  </div>
                )}
                {t.snippet && (
                  <div className="text-[12px] text-[#4A4B63] mt-1.5 line-clamp-2">
                    {t.snippet}
                  </div>
                )}
                <div className="text-[11px] text-[#8E8FA5] mt-2">
                  {formatDate(t.last_message_at)}
                </div>
              </div>
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => openThread(t.thread_id)}
                  data-testid={`email-open-${t.thread_id}`}
                  className="h-8 px-2.5 rounded-md text-[11.5px] font-semibold border border-[#C7D2FE] text-[#4F46E5] hover:bg-[#EEF2FF] flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> Ouvrir
                </button>
                <button
                  type="button"
                  onClick={() => createQuoteFromThread(t)}
                  data-testid={`email-create-quote-${t.thread_id}`}
                  title="Créer un devis pré-rempli à partir de cet échange"
                  className="h-8 px-2.5 rounded-md text-[11.5px] font-semibold border border-[#FED7AA] text-[#C2410C] hover:bg-[#FFF7ED] flex items-center gap-1"
                >
                  <FileText className="w-3 h-3" /> Devis
                </button>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
